import { useRef, useCallback, useEffect, useState } from 'react'
import * as signalR from '@microsoft/signalr'
import DOMPurify from 'dompurify'
import {
    ChatMessage,
    Assistant,
    ChatMetrics,
    ConnectionStatus,
    RouterResponse,
    DOMPURIFY_CONFIG,
} from '../types'

// Default server URL for EmpowerIQ streaming chat
const DEFAULT_SERVER_URL =
    'https://empoweriq-chat-streaming.azurewebsites.net/chatHub'

// Generate or retrieve a unique clientId for each browser session
const generateClientId = (): string => {
    const storedId = localStorage.getItem('eid-chat-clientId')
    if (storedId) return storedId

    const newId =
        Math.random().toString(36).substring(2, 15) +
        Math.random().toString(36).substring(2, 15)
    localStorage.setItem('eid-chat-clientId', newId)
    return newId
}

// Placeholder pattern used by pipeline variable substitution (e.g., #{CHATBOT_SERVER_URL}#)
const PLACEHOLDER_PATTERN = /^#\{.+\}#$/

// Validate server URL - returns error message if invalid, null if valid
const validateServerUrl = (url: string | undefined): string | null => {
    // Check if URL is empty or undefined
    if (!url || url.trim() === '') {
        return 'ChatBot server URL is not configured'
    }

    // Check if URL is a pipeline placeholder that wasn't replaced
    if (PLACEHOLDER_PATTERN.test(url)) {
        return `ChatBot server URL contains unresolved placeholder: ${url}`
    }

    // Check if URL is a valid format (should be https:// or wss://)
    try {
        const parsed = new URL(url)
        if (!['https:', 'wss:', 'http:', 'ws:'].includes(parsed.protocol)) {
            return `ChatBot server URL has invalid protocol: ${parsed.protocol}`
        }
    } catch {
        return `ChatBot server URL is not a valid URL: ${url}`
    }

    return null // URL is valid
}

// Self-closing HTML tags that don't need a closing tag
const SELF_CLOSING_TAGS = new Set([
    'br',
    'hr',
    'img',
    'input',
    'meta',
    'link',
    'area',
    'base',
    'col',
    'embed',
    'param',
    'source',
    'track',
    'wbr',
])

// Find a safe point to render HTML where all tags are properly closed
const findSafeRenderPoint = (
    html: string,
): { safeHtml: string; remainder: string } => {
    const tagStack: string[] = []
    let lastSafeIndex = 0
    let i = 0

    while (i < html.length) {
        if (html[i] === '<') {
            const tagEnd = html.indexOf('>', i)
            if (tagEnd === -1) break

            const tagContent = html.substring(i + 1, tagEnd).trim()

            if (tagContent.startsWith('/')) {
                const tagName = tagContent
                    .substring(1)
                    .split(/[\s/]/)[0]
                    .toLowerCase()
                if (
                    tagStack.length > 0 &&
                    tagStack[tagStack.length - 1] === tagName
                ) {
                    tagStack.pop()
                }
            } else if (
                !tagContent.startsWith('!') &&
                !tagContent.startsWith('?')
            ) {
                const tagName = tagContent.split(/[\s/>]/)[0].toLowerCase()
                const isSelfClosing =
                    tagContent.endsWith('/') || SELF_CLOSING_TAGS.has(tagName)

                if (!isSelfClosing && tagName) {
                    tagStack.push(tagName)
                }
            }

            i = tagEnd + 1
            if (tagStack.length === 0) {
                lastSafeIndex = i
            }
        } else {
            i++
            if (tagStack.length === 0) {
                lastSafeIndex = i
            }
        }
    }

    return {
        safeHtml: html.substring(0, lastSafeIndex),
        remainder: html.substring(lastSafeIndex),
    }
}

// Escape text so it is shown literally
const escapeAsText = (str: string): string => {
    if (!str) return ''
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
}

// Parse server JSON response for structured data
const parseServerResponse = (parsed: RouterResponse): string => {
    let finalHtml = ''

    if (parsed.Reasoning) {
        finalHtml += `<h6 style="color:var(--main-accent, #3b82f6);font-size:0.8em;margin-bottom:0.5rem;">Reasoning: ${parsed.Reasoning}</h6>`
    }

    const answerVal = parsed.Answer
    if (answerVal) {
        finalHtml += answerVal
    }

    const confVal = parsed.Confidence
    if (typeof confVal === 'number') {
        const confPercent = (confVal * 100).toFixed(1) + '%'
        finalHtml += `<div style="margin-top:5px; font-size:0.8em; color:var(--main-text-secondary, #666);">Confidence: ${confPercent}</div>`
    }

    return finalHtml
}

interface UseChatSignalRProps {
    serverUrl?: string
    accessToken?: string
    locale?: string
    tenantId?: string
    /** When true, the SignalR connection will be established. Set to true when chat widget is opened. */
    shouldConnect?: boolean
    /** Maximum number of connection retry attempts before giving up. Defaults to 3. */
    maxRetries?: number
}

export interface SendMessageOptions {
    /** When true, the message is sent to the server but not shown in the chat UI (e.g. for AI-Explain). */
    silent?: boolean
    /** When provided, this text is shown in the chat UI instead of the full message sent to the server. */
    displayMessage?: string
}

interface UseChatSignalRReturn {
    messages: ChatMessage[]
    connectionStatus: ConnectionStatus
    selectedAssistant: Assistant | null
    metrics: ChatMetrics
    isLoading: boolean
    /** Error message if the server URL configuration is invalid */
    configError: string | null
    sendMessage: (
        message: string,
        assistantId?: string | null,
        options?: SendMessageOptions,
    ) => void    
    clearSession: () => void
    cancelRequest: () => void
    newChat: () => void
}

export const useChatSignalR = ({
    serverUrl = DEFAULT_SERVER_URL,
    locale = 'en',
    tenantId = 'internal',
    shouldConnect = false,
    maxRetries = 3,
}: UseChatSignalRProps): UseChatSignalRReturn => {
    const connectionRef = useRef<signalR.HubConnection | null>(null)
    const clientIdRef = useRef<string>(generateClientId())
    const retryCountRef = useRef<number>(0)
    const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Mirror shouldConnect in a ref so sendMessage can read the live value
    // without stale closures (avoids adding shouldConnect to sendMessage deps).
    const shouldConnectRef = useRef(shouldConnect)
    shouldConnectRef.current = shouldConnect

    // Queue for messages sent before the connection is established.
    // Flushed immediately after connection.start() resolves.
    const pendingRef = useRef<
        Array<{
            message: string
            assistantId?: string | null
            options?: SendMessageOptions
        }>
    >([])

    const [messages, setMessages] = useState<ChatMessage[]>([])
    const [connectionStatus, setConnectionStatus] =
        useState<ConnectionStatus>('disconnected')
    const [selectedAssistant, setSelectedAssistant] =
        useState<Assistant | null>(null)
    const [metrics, setMetrics] = useState<ChatMetrics>({
        total: 0,
        success: 0,
        error: 0,
    })
    const [isLoading, setIsLoading] = useState(false)
    // Ref mirrors isLoading so SignalR event callbacks (closed over [] deps) can
    // read the live value without stale closures.
    const isLoadingRef = useRef(false)
    // Incremented when onclose fires while chat is still open, to trigger useEffect re-run
    const [reconnectTrigger, setReconnectTrigger] = useState(0)

    // Watchdog: if the server acknowledges a message but never completes it
    // (silent failure, OpenAI timeout, service bus drop, etc.) auto-recover after
    // this many milliseconds of inactivity.
    const RESPONSE_TIMEOUT_MS = 50_000
    const responseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    )

    const clearResponseTimeout = useCallback(() => {
        if (responseTimeoutRef.current) {
            clearTimeout(responseTimeoutRef.current)
            responseTimeoutRef.current = null
        }
    }, [])

    const startResponseTimeout = useCallback(() => {
        if (responseTimeoutRef.current) clearTimeout(responseTimeoutRef.current)
        responseTimeoutRef.current = setTimeout(() => {
            // Server never completed the response — recover gracefully
            isLoadingRef.current = false
            setIsLoading(false)
            if (currentMessageIdRef.current) {
                const stuckId = currentMessageIdRef.current
                setMessages((prev) =>
                    prev.map((m) =>
                        m.id === stuckId ? { ...m, isStreaming: false } : m,
                    ),
                )
                currentMessageIdRef.current = null
            }
            isStreamingRef.current = false
            streamBufferRef.current = ''
            setMessages((prev) => [
                ...prev,
                {
                    id: `msg-${Date.now()}-${Math.random()
                        .toString(36)
                        .slice(2, 9)}`,
                    role: 'assistant' as const,
                    content:
                        '<em>The server took too long to respond. Please try sending your message again.</em>',
                    timestamp: new Date(),
                },
            ])
        }, RESPONSE_TIMEOUT_MS)
    }, []) // stable — only touches refs and stable setState setters

    // Validate server URL and store any error
    const configError = validateServerUrl(serverUrl)

    // Streaming state refs
    const streamBufferRef = useRef<string>('')
    const isStreamingRef = useRef<boolean>(false)
    const currentMessageIdRef = useRef<string | null>(null)
    const nextBubbleIsRouterRef = useRef<boolean>(false)

    // Generate unique message ID
    const generateMessageId = () =>
        `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

    // Process HTML chunk for safe rendering
    const processHtmlChunk = useCallback((chunk: string): string => {
        streamBufferRef.current += chunk
        const { safeHtml, remainder } = findSafeRenderPoint(
            streamBufferRef.current,
        )

        let sanitizedSafeHtml = safeHtml
        if (typeof DOMPurify !== 'undefined') {
            sanitizedSafeHtml = DOMPurify.sanitize(safeHtml, DOMPURIFY_CONFIG)
        }

        const escapedRemainder = escapeAsText(remainder)
        return sanitizedSafeHtml + escapedRemainder
    }, [])

    // Update current streaming message
    const updateStreamingMessage = useCallback(
        (content: string, isComplete: boolean = false) => {
            if (!currentMessageIdRef.current) return

            setMessages((prev) =>
                prev.map((msg) =>
                    msg.id === currentMessageIdRef.current
                        ? {
                              ...msg,
                              content,
                              isStreaming: !isComplete,
                          }
                        : msg,
                ),
            )

            if (isComplete) {
                currentMessageIdRef.current = null
                isStreamingRef.current = false
                streamBufferRef.current = ''
            }
        },
        [],
    )

    // Create a new assistant message bubble
    const createAssistantMessage = useCallback(
        (initialContent: string = '', isRouterBubble: boolean = false) => {
            const msgId = generateMessageId()
            currentMessageIdRef.current = msgId
            isStreamingRef.current = true
            streamBufferRef.current = ''

            const newMessage: ChatMessage = {
                id: msgId,
                role: 'assistant',
                content: initialContent,
                timestamp: new Date(),
                isStreaming: true,
                isRouterBubble,
                isCollapsed: false,
            }

            setMessages((prev) => [...prev, newMessage])
            return msgId
        },
        [],
    )

    // Initialize SignalR connection - only when shouldConnect is true (chat widget is opened)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        // Don't connect if there's a configuration error (invalid URL, placeholder, etc.)
        if (configError) {
            console.warn('ChatBot SignalR:', configError)
            setConnectionStatus('invalid_config')
            return
        }

        // Don't connect if shouldConnect is false
        if (!shouldConnect) {
            // If we have an existing connection and shouldConnect becomes false, disconnect
            if (connectionRef.current) {
                connectionRef.current.stop()
                connectionRef.current = null
                setConnectionStatus('disconnected')
            }
            // Clear any pending retry timeout
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current)
                retryTimeoutRef.current = null
            }
            return
        }

        // If connection already exists and is not disconnected, don't create a new one
        // This prevents multiple connections in React Strict Mode (dev) where effects run twice
        if (
            connectionRef.current &&
            connectionRef.current.state !==
                signalR.HubConnectionState.Disconnected
        ) {
            return
        }

        const connection = new signalR.HubConnectionBuilder()
            .withUrl(serverUrl, {
                skipNegotiation: true,
                transport: signalR.HttpTransportType.WebSockets,
            })
            .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
            .configureLogging(signalR.LogLevel.Information)
            .build()

        connectionRef.current = connection

        // Handle MessageReceived - server acknowledged message
        connection.on('MessageReceived', () => {
            // If a previous message was still streaming (e.g. stale Service Bus
            // delivery while a new send was made), finalize its spinner now so
            // it doesn't stay stuck indefinitely.
            if (currentMessageIdRef.current) {
                const staleId = currentMessageIdRef.current
                setMessages((prev) =>
                    prev.map((m) =>
                        m.id === staleId ? { ...m, isStreaming: false } : m,
                    ),
                )
            }
            streamBufferRef.current = ''
            isStreamingRef.current = false
            currentMessageIdRef.current = null
            nextBubbleIsRouterRef.current = false
            isLoadingRef.current = true
            setIsLoading(true)
            startResponseTimeout() // Arm watchdog: server must respond within timeout
        })

        // Handle ReceiveMessage - streaming chunks
        connection.on('ReceiveMessage', (message: string) => {
            startResponseTimeout() // Reset watchdog — server is still sending data
            if (!message) return

            // Check for [COMPLETE_JSON] marker
            if (message.startsWith('[COMPLETE_JSON]')) {
                try {
                    const jsonStr = message.substring('[COMPLETE_JSON]'.length)
                    const parsed: RouterResponse = JSON.parse(jsonStr)

                    const answerHtml = parseServerResponse(parsed)
                    const sanitized = DOMPurify.sanitize(
                        answerHtml,
                        DOMPURIFY_CONFIG,
                    )
                    updateStreamingMessage(sanitized, true)
                } catch (e: unknown) {
                    console.error('Failed to parse complete JSON:', e)
                }
                return
            }

            // Handle status messages
            if (
                message === 'Thinking...' ||
                message.startsWith('Getting the information')
            ) {
                if (!isStreamingRef.current) {
                    createAssistantMessage(
                        '<div class="loading-message">Just a sec… fetching information...</div>',
                        nextBubbleIsRouterRef.current,
                    )
                    nextBubbleIsRouterRef.current = false
                }
                return
            }

            // Start streaming if not already
            if (!isStreamingRef.current) {
                createAssistantMessage('', nextBubbleIsRouterRef.current)
                nextBubbleIsRouterRef.current = false
            }

            // Process and display the chunk
            const processedContent = processHtmlChunk(message)
            updateStreamingMessage(processedContent)
        })

        // Handle CompleteMessage - bubble or full-response finished.
        // The server sends two kinds of CompleteMessage:
        //   "" (empty)  — intermediate: closes the current router/status bubble only
        //   "Message processing completed." — final: also clears the loading state
        // Only clearing isLoading on the final signal prevents it from going false
        // mid-flow (between router phase and specialized phase).
        connection.on('CompleteMessage', () => {
            clearResponseTimeout() // Disarm watchdog — server replied normally

            // Flush any remaining buffered content into the current message.
            if (currentMessageIdRef.current && streamBufferRef.current) {
                const sanitized = DOMPurify.sanitize(
                    streamBufferRef.current,
                    DOMPURIFY_CONFIG,
                )
                updateStreamingMessage(sanitized, true)
            }

            // Unconditionally clear isStreaming on every message — guards against
            // cases where currentMessageIdRef was already reset (watchdog fired,
            // MessageReceived race) but the bubble was left with isStreaming:true.
            setMessages((prev) =>
                prev.map((m) =>
                    m.isStreaming ? { ...m, isStreaming: false } : m,
                ),
            )

            currentMessageIdRef.current = null
            isStreamingRef.current = false
            streamBufferRef.current = ''
            isLoadingRef.current = false
            setIsLoading(false)
        })

        // Handle metrics
        connection.on(
            'ReceiveMetrics',
            (total: number, success: number, error: number) => {
                setMetrics({ total, success, error })
            },
        )

        // Handle assistant selection
        connection.on(
            'AssistantSelected',
            (assistantId: string, assistantName: string) => {
                setSelectedAssistant({ id: assistantId, name: assistantName })
            },
        )

        // Handle router bubble marker - finalize the current streaming bubble first,
        // then signal that the next bubble will be the router's formatted answer
        connection.on('StartRouterBubble', () => {
            if (currentMessageIdRef.current) {
                // Mark the current in-progress bubble as done (removes its spinner)
                // without wiping its already-streamed content
                const finishedId = currentMessageIdRef.current
                setMessages((prev) =>
                    prev.map((msg) =>
                        msg.id === finishedId
                            ? { ...msg, isStreaming: false }
                            : msg,
                    ),
                )
                currentMessageIdRef.current = null
            }
            isStreamingRef.current = false
            streamBufferRef.current = ''
            nextBubbleIsRouterRef.current = true
        })

        // Fired while SignalR's built-in retry is active (server temporarily down)
        connection.onreconnecting(() => {
            setConnectionStatus('connecting')
        })

        // Fired when SignalR successfully reconnects after a drop
        connection.onreconnected(() => {
            setConnectionStatus('connected')
            retryCountRef.current = 0
        })

        // Fired when all SignalR built-in retries are exhausted.
        // Increment reconnectTrigger so the useEffect re-runs and creates a fresh connection,
        // keeping the chat alive as long as the widget stays open.
        connection.onclose(() => {
            setConnectionStatus('disconnected')
            connectionRef.current = null
            setReconnectTrigger((t) => t + 1)
        })

        const startConnection = async () => {
            try {
                setConnectionStatus('connecting')
                await connection.start()
                setConnectionStatus('connected')
                // Reset retry count on successful connection
                retryCountRef.current = 0

                // Flush messages that were queued while the connection was establishing
                const queued = pendingRef.current.splice(0)
                for (const item of queued) {
                    const silent = item.options?.silent === true
                    if (!silent) {
                        setMessages((prev) => [
                            ...prev,
                            {
                                id: generateMessageId(),
                                role: 'user' as const,
                                content: item.message,
                                timestamp: new Date(),
                            },
                        ])
                    }
                    connection
                        .invoke(
                            'SendMessageToServer',
                            clientIdRef.current,
                            item.message,
                            tenantId,
                            item.assistantId || null,
                        )
                        .catch((err: unknown) => {
                            console.error('Error sending queued message:', err)
                        })
                }
            } catch (err: unknown) {
                console.error('SignalR connection error:', err)
                // Check if we've exceeded max retries
                if (retryCountRef.current >= maxRetries) {
                    console.warn(
                        `SignalR connection failed after ${maxRetries} attempts. Stopping reconnection.`,
                    )
                    setConnectionStatus('error')
                    return
                }
                // Increment retry count and retry after delay
                retryCountRef.current += 1
                console.log(
                    `SignalR connection failed. Retrying... (attempt ${retryCountRef.current}/${maxRetries})`,
                )
                setConnectionStatus('connecting')
                retryTimeoutRef.current = setTimeout(startConnection, 2000)
            }
        }

        startConnection()

        return () => {
            if (responseTimeoutRef.current) {
                clearTimeout(responseTimeoutRef.current)
                responseTimeoutRef.current = null
            }
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current)
                retryTimeoutRef.current = null
            }
            connection.stop()
            connectionRef.current = null
        }
    }, [shouldConnect, serverUrl, maxRetries, configError, reconnectTrigger])

    // Send message to server
    const sendMessage = useCallback(
        (
            userMessage: string,
            assistantId?: string | null,
            options?: SendMessageOptions,
        ) => {
            const silent = options?.silent === true
            if (!silent) {
            // Add the user message to the chat regardless of connection state
            const userMsg: ChatMessage = {
                id: generateMessageId(),
                role: 'user',
                content: options?.displayMessage ?? userMessage,
                timestamp: new Date(),
            }
            setMessages((prev) => [...prev, userMsg])
        }
            const connection = connectionRef.current
            if (
                !connection ||
                connection.state !== signalR.HubConnectionState.Connected
            ) {
                if (shouldConnectRef.current) {
                    // Connection is being established — queue and send once ready
                    pendingRef.current.push({ message: userMessage, assistantId, options })
                    return
                }
                // Not connected and not connecting — show a visible error bubble
                const errMsg: ChatMessage = {
                    id: generateMessageId(),
                    role: 'assistant',
                    content:
                        '<em>Not connected to the server. Please wait for reconnection or reopen the chat.</em>',
                    timestamp: new Date(),
                }
                setMessages((prev) => [...prev, errMsg])
                return
            }

            // Send to server
            connection
                .invoke(
                    'SendMessageToServer',
                    clientIdRef.current,
                    userMessage,
                    tenantId,
                    assistantId || selectedAssistant?.id || null,
                )
                .catch((err: unknown) => {
                    console.error('Error sending message:', err)
                    setMessages((prev) => [
                        ...prev,
                        {
                            id: generateMessageId(),
                            role: 'assistant',
                            content:
                                '<em>Failed to send message. Please try again.</em>',
                            timestamp: new Date(),
                        },
                    ])
                })
        },
        [tenantId, selectedAssistant],
    )

    // Clear session
    const clearSession = useCallback(() => {
        const connection = connectionRef.current
        if (
            !connection ||
            connection.state !== signalR.HubConnectionState.Connected
        ) {
            return
        }

        setSelectedAssistant(null)
        connection.invoke('ClearSession').catch((err: unknown) => {
            console.error('Error clearing session:', err)
        })
    }, [])

    // Cancel current request
    const cancelRequest = useCallback(() => {
        clearResponseTimeout()
        const connection = connectionRef.current
        if (
            !connection ||
            connection.state !== signalR.HubConnectionState.Connected
        ) {
            return
        }

        isLoadingRef.current = false
        setIsLoading(false)
        connection.invoke('CancelRequest').catch((err: unknown) => {
            console.error('Error cancelling request:', err)
        })
    }, [clearResponseTimeout])

    // Start new chat
    const newChat = useCallback(() => {
        clearResponseTimeout()
        setMessages([])
        streamBufferRef.current = ''
        isStreamingRef.current = false
        currentMessageIdRef.current = null
        isLoadingRef.current = false
        setIsLoading(false)
        // Stop the connection so we get a fresh connection ID on reconnect.
        // Any in-flight Service Bus messages for the old connection ID will be
        // delivered to a dead connection and silently dropped, preventing stale
        // responses from appearing in the new chat session.
        // The server cleans up the session automatically via OnDisconnectedAsync.
        connectionRef.current?.stop()
    }, [clearResponseTimeout])

    return {
        messages,
        connectionStatus,
        selectedAssistant,
        metrics,
        isLoading,
        configError,
        sendMessage,
        clearSession,
        cancelRequest,
        newChat,
    }
}

export default useChatSignalR
